import React from 'react';
import { render } from 'react-dom';
import { Provider as StoreProvider } from 'react-redux';
import { compileConfigForAppdomain } from '/app/dist/18.config.js';
import { compileLocalesForAppdomain } from '/app/dist/18.js';
import { Appdomain } from 'shared/enums';

import { initSync, getLoggerInstance } from '@pegasus/common/client/lib/logging';
import { ConfigProvider } from 'client/config';
import { GlobalStyle } from './shared/styles';
import { AppRoot } from './AppRootLegacy';
import { TranslateProvider } from '@pegasus/translation/components';

import { configureStore } from './shared/redux';
import { registerListener } from './shared/legacy/flash-messenger';
import { getEnv } from 'shared/helpers';
import { injectAccessors } from 'config/accessors';
import { isWebView } from './helpers';
import { init as initSentry } from './helpers/sentry';

const appdomain = parseInt(
    (typeof window !== 'undefined' && window.APPDOMAIN) || process.env.APPDOMAIN || '17',
    0,
) as Appdomain;

const config = injectAccessors(compileConfigForAppdomain(appdomain, getEnv()));
const locale = config.locale[appdomain];

initSentry(
    {
        dsn: config.sentry && config.sentry.dsn,
        enabled: config.isEnabled('sentry'),
    },
    {
        appdomain,
        clientId: window.CLIENT_ID,
        flowId: window.INITIAL_FLOW_ID,
        isWebView: isWebView(),
    },
);

const logger = getLoggerInstance();
logger.info('Initialising logger sync');
initSync(2000);

if (!__DEV__) {
    // eslint-disable-next-line
    __webpack_public_path__ = config.getUrl('js');
}

logger.info(`Building translations for Appdomain ${appdomain}`);
const translations = compileLocalesForAppdomain(appdomain);

const store = configureStore();

registerListener(store);

const renderApp = (container: HTMLElement | null) =>
    render(
        <StoreProvider store={store}>
            <ConfigProvider config={config}>
                <TranslateProvider translations={translations}>
                    <GlobalStyle />
                    <AppRoot />
                </TranslateProvider>
            </ConfigProvider>
        </StoreProvider>,
        container,
    );

const startApp = () => {
    logger.info('Starting app');

    const root = document.getElementById('react-container');

    if (__DEV__) {
        logger.info('Entering development mode');

        /* eslint-disable-next-line global-require */
        const RedBox = require('redbox-react');
        try {
            renderApp(root);
        } catch (e) {
            logger.error(e);
            render(<RedBox error={e} />, root);
        }
    } else {
        renderApp(root);
    }
};

const polyfills = [];

if (typeof window.fetch !== 'function') {
    polyfills.push(import(/* webpackChunkName: "other-polyfills" */ 'whatwg-fetch'));
}

if (window.Intl && !window.Intl.PluralRules) {
    polyfills.push(import(/* webpackChunkName: "fr.IntlBundle" */ 'intl-pluralrules'));
}

if (window.Intl && !window.Intl.RelativeTimeFormat) {
    polyfills.push(
        import(/* webpackChunkName: "fr.IntlBundle" */ '@formatjs/intl-relativetimeformat/polyfill'),
    );
    polyfills.push(
        import(
            /* webpackChunkName: "fr.IntlBundle" */ `@formatjs/intl-relativetimeformat/dist/locale-data/${
                'fr'
            }`
        ),
    );
}

if (!window.Intl) {
    polyfills.push(
        import(/* webpackChunkName: "fr.IntlBundle" */ 'intl').then(intl => {
            window.Intl = intl.default;
            return Promise.all([
                locale
                    ? import(
                          /* webpackChunkName: "fr.IntlBundle" */ `intl/locale-data/jsonp/${
                              'fr'
                          }`
                      )
                    : Promise.resolve(),
                import(/* webpackChunkName: "fr.IntlBundle" */ 'intl-pluralrules'),
            ]);
        }),
    );
}

if (!window.matchMedia) {
    polyfills.push(import(/* webpackChunkName: "match-media" */ 'match-media'));
}

Promise.all(polyfills).then(() => {
    startApp();
});
